<template>
  <f7-block
    v-if="visible"
    class="setup-checklist no-margin-horizontal"
    strong
    inset
  >
    <!-- Readiness & Lifecycle Hero Card -->
    <f7-card class="setup-summary" :padding="false">
      <f7-card-content class="setup-summary-inner">
        <div class="setup-summary-head">
          <div class="setup-summary-id">
            <div class="setup-avatar setup-avatar--fixed">
              <f7-icon material="published_with_changes" :size="20" />
            </div>
            <div>
              <h2 class="setup-title">{{ $t("groupSetup.title") }}</h2>
              <p class="setup-subtitle">{{ progressLabel }}</p>
            </div>
          </div>
          <span class="setup-pct">{{ progressPct }}%</span>
        </div>

        <div class="setup-progress">
          <div class="setup-segments">
            <span
              v-for="n in totalSegmentCount"
              :key="n"
              class="setup-segment"
              :class="segmentClass(n)"
            />
          </div>
          <div class="stage-legend">
            <span class="stage-legend-item stage-legend-item--primary">
              <f7-icon material="check_circle" :size="12" />
              {{ $t("groupSetup.stageCreation") }}
            </span>
            <span class="stage-legend-item stage-legend-item--primary">
              <f7-icon material="chat" :size="12" />
              {{ $t("groupSetup.stageSocial") }}
            </span>
            <span class="stage-legend-item stage-legend-item--secondary">
              <f7-icon material="account_balance" :size="12" />
              {{ $t("groupSetup.stageFinance") }}
            </span>
            <span class="stage-legend-item stage-legend-item--later">{{ $t("groupSetup.stageKyc") }}</span>
            <span class="stage-legend-item stage-legend-item--later">{{ $t("groupSetup.stageReady") }}</span>
          </div>
        </div>

        <div class="setup-info">
          <f7-icon material="lock_person" :size="20" class="setup-info-icon" />
          <p class="setup-info-text">
            {{ $t("groupSetup.infoMicrocopy") }}
          </p>
        </div>
      </f7-card-content>
    </f7-card>

    <!-- Checklist Tasks Header -->
    <div class="setup-items-head">
      <div class="setup-items-title">
        <h3>{{ $t("groupSetup.roadmap") }}</h3>
        <span class="setup-dot" />
      </div>
      <span class="setup-count">{{ $t("groupSetup.actionsRequired", { n: pendingCount }) }}</span>
    </div>

    <div class="setup-items">
      <article
        v-for="item in items"
        :key="item.key"
        class="setup-item-card"
        :class="{ done: item.done, 'setup-item-blocking': item.blocking }"
        role="button"
        tabindex="0"
        :aria-label="item.title"
        @click="runItem(item)"
        @keydown.enter="runItem(item)"
        @keydown.space.prevent="runItem(item)"
      >
        <div v-if="item.blocking && !item.done" class="setup-blocking-bar" />
        <div class="setup-item-top">
          <div class="setup-item-id">
            <div
              class="setup-avatar"
              :class="itemIconClass(item)"
            >
              <f7-icon :material="itemIcon(item)" :size="20" />
            </div>
            <div>
              <h4 class="setup-item-title">{{ item.title }}</h4>
              <p class="setup-item-tag" :class="itemSubClass(item)">{{ item.done ? $t('groupSetup.stepDoneLabel') : item.tagLine }}</p>
            </div>
          </div>
          <f7-badge
            class="setup-status-badge"
            :class="itemBadgeClass(item)"
          >
            {{ itemBadge(item) }}
          </f7-badge>
        </div>
        <p class="setup-item-desc">{{ item.desc }}</p>
        <div class="setup-item-foot">
          <span v-if="!item.done" class="setup-item-time">
            <f7-icon material="timer" :size="16" />
            {{ $t('groupSetup.timeShort') }}
          </span>
          <f7-button
            round
            raised
            class="setup-cta"
            :class="itemCtaClass(item)"
            @click.stop="runItem(item)"
          >
            {{ item.done ? $t('groupSetup.stepDone') : item.cta }}
            <f7-icon :material="item.done ? 'check' : 'arrow_forward'" :size="18" />
          </f7-button>
        </div>
      </article>
    </div>

    <div class="setup-footer">
      <f7-link small @click="dismiss">
        <f7-icon f7="xmark" size="12" /> {{ $t("groupSetup.dismiss") }}
      </f7-link>
    </div>
  </f7-block>
</template>

<script setup>
import { computed, ref } from 'vue'
import { f7 } from 'framework7-vue'
import i18n from '../../js/i18n.js'
const { t } = i18n.global

const props = defineProps({
  groupId: { type: String, required: true },
  group: { type: Object, default: null },
  canManage: { type: Boolean, default: false },
  membersCount: { type: Number, default: 1 },
  campaignCount: { type: Number, default: 0 },
})
const emit = defineEmits(['banner', 'logo', 'marketplace', 'recipient', 'publish'])

const FINANCIAL_TYPES = ['TONTINE_MUTUAL', 'MUTUAL_AID_ASSOCIATION', 'FARMERS_COOPERATIVE']
const BUSINESS_TYPES = [
  'COLLEAGUES', 'TRADERS_INFORMAL', 'PROFESSIONALS_FORMAL',
  'FARMERS_COOPERATIVE', 'ENTREPRENEURS', 'UNION_PROFESSIONAL_ORDER', 'ADVERTISERS_BRANDS',
]

const DISMISS_KEY = 'avocash:setup-checklist:dismissed:'

const types = computed(() => props.group?.types || [])
const financeGroup = computed(
  () =>
    props.group?.starterMode === 'MONEY' ||
    types.value.some((c) => FINANCIAL_TYPES.includes(c)),
)
const shopRecommended = computed(
  () =>
    props.group?.starterMode === 'COMMERCE' ||
    types.value.some((c) => BUSINESS_TYPES.includes(c)),
)
const marketplaceEnabled = computed(() => {
  const mode = props.group?.marketplaceMode
  // Backend stores marketplace state as ModeBehavior; "OFF"/unset = disabled
  return !!mode && mode !== 'OFF'
})

const items = computed(() => {
  const list = [
    {
      key: 'banner',
      icon: 'photo',
      label: 'addBanner',
      descKey: 'addBannerDesc',
      done: !!props.group?.banner,
      action: 'banner',
      tagLine: t('groupSetup.customizeTag'),
      cta: t('groupSetup.addButton'),
      status: 'optional',
    },
    {
      key: 'logo',
      icon: 'photo_camera',
      label: 'addLogo',
      descKey: 'addLogoDesc',
      done: !!props.group?.logo,
      action: 'logo',
      tagLine: t('groupSetup.customizeTag'),
      cta: t('groupSetup.addButton'),
      status: 'optional',
    },
    {
      key: 'invite',
      icon: 'person_add',
      label: 'inviteMembers',
      descKey: 'inviteMembersDesc',
      done: props.membersCount >= 3,
      href: `/groups/${props.groupId}/members/import`,
      tagLine: t('groupSetup.foundersTag'),
      cta: t('groupSetup.inviteButton'),
      status: 'done-or-action',
    },
    {
      key: 'campaign',
      icon: 'campaign',
      label: 'createCampaign',
      descKey: 'createCampaignDesc',
      done: props.campaignCount > 0,
      href: `/groups/${props.groupId}/campaigns/create`,
      tagLine: t('groupSetup.titleTag'),
      cta: t('groupSetup.startButton'),
      status: 'action',
    },
  ]
  if (shopRecommended.value) {
    list.push({
      key: 'shop',
      icon: 'storefront',
      label: 'enableShop',
      descKey: 'enableShopDesc',
      done: marketplaceEnabled.value,
      action: 'marketplace',
      tagLine: t('groupSetup.shopTag'),
      cta: t('groupSetup.enableButton'),
      status: 'action',
    })
  }
  if (financeGroup.value) {
    list.push({
      key: 'recipient',
      icon: 'verified_user',
      label: 'verifyRecipient',
      descKey: 'verifyRecipientDesc',
      done: !!props.group?.payoutSetup,
      action: 'recipient',
      tagLine: t('groupSetup.recipientTag'),
      cta: t('groupSetup.verifyButton'),
      status: 'blocking',
      blocking: true,
    })
  }
  const isDraft = props.group?.status === 'DRAFT'
  if (isDraft) {
    list.push({
      key: 'publish',
      icon: 'published_with_changes',
      label: 'publishGroup',
      descKey: 'publishGroupDesc',
      done: false,
      action: 'publish',
      tagLine: t('groupSetup.titleTag'),
      cta: t('groupSetup.publishButton'),
      status: 'action',
      blocking: true,
    })
  }
  return list.map((it) => ({
    ...it,
    title: t(`groupSetup.${it.label}`),
    desc: t(`groupSetup.${it.descKey}`),
  }))
})

const doneCount = computed(() => items.value.filter((i) => i.done).length)
const pendingCount = computed(() => items.value.filter((i) => !i.done).length)
const total = computed(() => items.value.length)
const progressPct = computed(() =>
  total.value ? Math.round((doneCount.value / total.value) * 100) : 0,
)
const totalSegmentCount = computed(() => 5)
function segmentClass(n) {
  if (n < 3) return 'setup-segment--done'
  if (n === 3) return 'setup-segment--current'
  return 'setup-segment--todo'
}
const progressLabel = computed(() =>
  t('groupSetup.subtitle', { done: doneCount.value, total: total.value }),
)

function itemIcon(item) {
  if (item.done) return 'done_all'
  return item.icon
}
function itemIconClass(item) {
  if (item.done) return 'setup-avatar--done'
  if (item.status === 'blocking') return 'setup-avatar--blocking'
  if (item.status === 'optional') return 'setup-avatar--optional'
  return 'setup-avatar--action'
}
function itemSubClass(item) {
  if (item.done) return 'setup-tag--done'
  if (item.status === 'blocking') return 'setup-tag--blocking'
  if (item.status === 'optional') return 'setup-tag--muted'
  return 'setup-tag--secondary'
}
function itemBadge(item) {
  if (item.done) return t('groupSetup.statusDone')
  const map = {
    blocking: 'groupSetup.statusBlocking',
    optional: 'groupSetup.statusOptional',
    action: 'groupSetup.statusRequired',
    'done-or-action': 'groupSetup.statusRequired',
  }
  return t(map[item.status] || 'groupSetup.statusRequired')
}
function itemBadgeClass(item) {
  if (item.done) return 'setup-badge--done'
  if (item.status === 'blocking') return 'setup-badge--blocking'
  if (item.status === 'optional') return 'setup-badge--optional'
  return 'setup-badge--action'
}
function itemCtaClass(item) {
  if (item.done) return 'setup-cta--muted'
  if (item.status === 'blocking') return 'setup-cta--strong'
  if (item.status === 'optional') return 'setup-cta--muted'
  return 'setup-cta--primary'
}

const dismissed = ref(false)
function isDismissed() {
  try {
    return localStorage.getItem(DISMISS_KEY + props.groupId) === '1'
  } catch (e) {
    return false
  }
}
function dismiss() {
  dismissed.value = true
  try {
    localStorage.setItem(DISMISS_KEY + props.groupId, '1')
  } catch (e) {
    // ignore
  }
}

const visible = computed(
  () =>
    props.canManage &&
    !dismissed.value &&
    !isDismissed() &&
    items.value.length > 0 &&
    doneCount.value < total.value,
)

function runItem(item) {
  if (item.done) return
  if (item.action === 'banner') return emit('banner')
  if (item.action === 'logo') return emit('logo')
  if (item.action === 'marketplace') return emit('marketplace')
  if (item.action === 'recipient') return emit('recipient')
  if (item.action === 'publish') return emit('publish')
  if (item.href) f7.views.main.router.navigate(item.href, {props: {group: props.group, groupId: props.groupId}})
}
</script>

<style scoped>
/* All colors resolve through theme-flipping tokens (defined for :root and
   .dark in app.css) — one rule set serves light and dark mode. */
.setup-checklist {
  margin-top: 12px;
  background: transparent;
  border: none;
  color: var(--f7-on-surface, #161d18);
}

/* Hero summary card */
.setup-summary {
  margin: 0 0 12px;
  background: var(--f7-card-bg-color, #fff);
  border-radius: 16px;
  box-shadow: 0 2px 8px -2px rgba(26, 107, 66, 0.06), 0 1px 3px rgba(44, 52, 46, 0.06);
}
.setup-summary-inner {
  display: flex;
  flex-direction: column;
}
.setup-summary-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.setup-summary-id {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.setup-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
}
.setup-subtitle {
  margin: 0;
  font-size: 11px;
  font-weight: 500;
  color: var(--f7-on-surface-variant, #3f4941);
}
.setup-pct {
  font-size: 20px;
  font-weight: 600;
  color: var(--f7-theme-color, #1a6b42);
  flex-shrink: 0;
}

/* Progress segments + stage legend */
.setup-progress {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
}
.setup-segments {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
  height: 8px;
  width: 100%;
}
.setup-segment {
  border-radius: 999px;
  height: 100%;
}
.setup-segment--done {
  background: var(--f7-theme-color, #1a6b42);
}
.setup-segment--current {
  background: var(--f7-secondary, #755b00);
  animation: setup-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
.setup-segment--todo {
  background: var(--f7-surface-container-highest, #dce5dc);
}
@keyframes setup-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
}
.stage-legend {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 10px;
  padding-top: 2px;
  overflow-x: auto;
  scrollbar-width: none;
}
.stage-legend::-webkit-scrollbar {
  display: none;
}
.stage-legend-item {
  display: flex;
  align-items: center;
  gap: 2px;
  white-space: nowrap;
}
.stage-legend-item--primary {
  color: var(--f7-theme-color, #1a6b42);
  font-weight: 600;
}
.stage-legend-item--secondary {
  color: var(--f7-secondary, #755b00);
  font-weight: 600;
}
.stage-legend-item--later {
  color: var(--f7-on-surface-variant, #6f7a71);
}

/* Info microcopy box */
.setup-info {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 12px;
  padding: 12px;
  border-radius: 12px;
  background: var(--f7-surface-container-low, #edf6ed);
}
.setup-info-icon {
  color: var(--f7-theme-color, #1a6b42);
  flex-shrink: 0;
  margin-top: 2px;
}
.setup-info-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  color: var(--f7-on-surface-variant, #3f4941);
}

/* Checklist header */
.setup-items-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 12px 4px 8px;
}
.setup-items-title {
  display: flex;
  align-items: center;
  gap: 6px;
}
.setup-items-title h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}
.setup-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--f7-secondary, #ffd857);
}
.setup-count {
  font-size: 12px;
  font-weight: 600;
  color: var(--f7-theme-color, #1a6b42);
}

/* Task cards */
.setup-items {
  display: flex;
  flex-direction: column;
}
.setup-item-card {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
  padding: 14px;
  border-radius: 16px;
  background: var(--f7-card-bg-color, #fff);
  box-shadow: 0 2px 8px -2px rgba(26, 107, 66, 0.06), 0 1px 3px rgba(44, 52, 46, 0.06);
  cursor: pointer;
  transition: transform 0.1s ease, box-shadow 0.2s ease;
}
.setup-item-card:active {
  transform: scale(0.99);
}
.setup-blocking-bar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  background: var(--f7-theme-color, #1a6b42);
}
.setup-item-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}
.setup-item-id {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-left: 4px;
  min-width: 0;
}
.setup-item-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
}
.setup-item-tag {
  margin: 0;
  font-size: 11px;
  font-weight: 500;
}
.setup-item-desc {
  margin: 0;
  padding-left: 4px;
  font-size: 14px;
  line-height: 1.45;
  color: var(--f7-on-surface-variant, #3f4941);
}
.setup-item-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  padding-left: 4px;
  padding-top: 2px;
}
.setup-item-time {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: var(--f7-on-surface-variant, #3f4941);
}
.setup-item-time .f7-icon {
  color: var(--f7-secondary, #755b00);
}

/* Avatars (icon circles) — M3 "fixed" pair stays constant in both themes */
.setup-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.setup-avatar--fixed {
  background: #a5f4bf;
  color: #0e3722;
}
.setup-avatar--done {
  background: #a5f4bf;
  color: #0e3722;
}
.setup-avatar--blocking {
  background: var(--f7-error-container, #fbdddc);
  color: var(--f7-error, #ba1a1a);
}
.setup-avatar--optional {
  background: var(--f7-surface-container, #e8f0e7);
  color: var(--f7-on-surface-variant, #3f4941);
}
.setup-avatar--action {
  background: color-mix(in srgb, var(--f7-theme-color) 12%, var(--f7-card-bg-color, #fff));
  color: var(--f7-theme-color, #1a6b42);
}

/* Status badges */
.setup-status-badge {
  min-width: 0;
  font-weight: 600;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  white-space: nowrap;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
}
.setup-badge--done {
  background: #a5f4bf;
  color: #0e3722;
}
.setup-badge--blocking {
  background: var(--f7-error-container, #fbdddc);
  color: var(--f7-on-error-container, #400101);
}
.setup-badge--optional {
  background: var(--f7-surface-container-low, #edf6ed);
  color: var(--f7-on-surface-variant, #3f4941);
}
.setup-badge--action {
  background: color-mix(in srgb, var(--f7-secondary) 22%, var(--f7-card-bg-color, #fff));
  color: var(--f7-secondary, #755b00);
}

/* Tag lines under item titles */
.setup-tag--done {
  color: var(--f7-theme-color, #1a6b42);
}
.setup-tag--blocking {
  color: var(--f7-error, #ba1a1a);
}
.setup-tag--muted {
  color: var(--f7-on-surface-variant, #3f4941);
}
.setup-tag--secondary {
  color: var(--f7-secondary, #755b00);
}

/* CTAs — driven through F7 button CSS vars (no !important wars) */
.setup-cta {
  margin: 0;
  font-weight: 600;
}
.setup-cta--primary {
  --f7-button-bg-color: var(--f7-theme-color, #1a6b42);
  --f7-button-text-color: var(--f7-on-primary, #fff);
}
.setup-cta--muted {
  --f7-button-bg-color: var(--f7-surface-container, #e8f0e7);
  --f7-button-text-color: var(--f7-on-surface-variant, #3f4941);
}
.setup-cta--strong {
  --f7-button-bg-color: var(--f7-surface-container-high, #e2eae1);
  --f7-button-text-color: var(--f7-on-surface, #161d18);
}

/* Footer dismiss */
.setup-footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
}
.setup-footer :deep(.link) {
  font-size: 12px;
}
</style>
